<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useResize } from '@/composables/useResize'
import { useChat } from '@/composables/useChat'
import { useThreads } from '@/composables/useThreads'
import { useCitations } from '@/composables/useCitations'
import AppHeader from './AppHeader.vue'
import ThreadList from '@/components/sidebar/ThreadList.vue'
import ChatPanel from '@/components/chat/ChatPanel.vue'
import SourcesPanel from '@/components/sources/SourcesPanel.vue'
import CitationsPanel from '@/components/citations/CitationsPanel.vue'
import CitationHovercard from '@/components/chat/CitationHovercard.vue'
import ResizeHandle from './ResizeHandle.vue'

const { sources } = useChat()
const { activeThreadId } = useThreads()
const { activeCitations, setActiveCitations, isPanelOpen } = useCitations()

const sidebarWidth = ref(260)
const sourcesWidth = ref(360)
const citationsWidth = ref(320)
const sourcesOpen = ref(false)

// Switching threads must not leak the previous conversation's references into
// the new one. Clear the shared reference set and collapse the sources pane;
// the newly rendered answer's bubbles will repopulate references if it has any.
watch(activeThreadId, () => {
  setActiveCitations([])
  sourcesOpen.value = false
})

const sourcesAvailable = computed(() => sources.value.length > 0)
// References pane is shown (desktop) whenever the answer has citations and the
// user hasn't closed it.
const referencesAvailable = computed(() => activeCitations.value.length > 0 && isPanelOpen.value)

const { dragging: sidebarDragging, onPointerDown: onSidebarDown } = useResize('horizontal', (delta) => {
  sidebarWidth.value = Math.min(420, Math.max(180, sidebarWidth.value + delta))
})

const { dragging: sourcesDragging, onPointerDown: onSourcesDown } = useResize('horizontal', (delta) => {
  sourcesWidth.value = Math.min(window.innerWidth * 0.5, Math.max(260, sourcesWidth.value - delta))
})

const { dragging: citationsDragging, onPointerDown: onCitationsDown } = useResize('horizontal', (delta) => {
  citationsWidth.value = Math.min(window.innerWidth * 0.5, Math.max(260, citationsWidth.value - delta))
})
</script>

<template>
  <div class="flex flex-col h-screen overflow-hidden bg-[#f5f5f5] text-neutral-800">
    <AppHeader />
    <div class="flex flex-1 overflow-hidden">
      <ThreadList :style="{ width: sidebarWidth + 'px' }" class="shrink-0" />
      <ResizeHandle direction="horizontal" :dragging="sidebarDragging" @pointerdown="onSidebarDown" />
      <ChatPanel
        class="flex-1 min-w-[200px]"
        :sources-available="sourcesAvailable"
        :sources-open="sourcesOpen"
        @toggle-sources="sourcesOpen = !sourcesOpen"
      />
      <template v-if="sourcesOpen && sourcesAvailable">
        <ResizeHandle direction="horizontal" :dragging="sourcesDragging" @pointerdown="onSourcesDown" />
        <SourcesPanel :style="{ width: sourcesWidth + 'px' }" class="shrink-0" />
      </template>
      <template v-if="referencesAvailable">
        <ResizeHandle direction="horizontal" :dragging="citationsDragging" @pointerdown="onCitationsDown" class="hidden md:flex" />
        <CitationsPanel :style="{ width: citationsWidth + 'px' }" class="shrink-0 hidden md:flex" />
      </template>
    </div>
    <CitationHovercard />
  </div>
</template>
